import Lottie, { AnimationItem } from "lottie-web";
import { SpecialEffectIconUsed } from "../components/ChannelsCom/SpecialEffect";
import { useCallback, useEffect, useRef } from "react";
import {
    FaceChangeJSON,
    TornadoJSON,
    WelcomeJSON,
    CryJSON,
    HeartJSON,
    BombJSON,
    SkateJSON,
    CelebrateJSON,
} from "../components/SvgIconCom";

export const SpecialEffectRenderDo: Record<
    SpecialEffectIconUsed,
    ((animation: AnimationItem) => void) | undefined
> = {
    FaceChangeIcon: animation => {
        animation.setSpeed(2);
    },
    TornadoIcon: animation => {
        const frameRate = animation.frameRate;
        const startTime = 8;
        const endTime = 10;
        const startFrame = startTime * frameRate;
        const endFrame = endTime * frameRate;
        animation.playSegments([startFrame, endFrame], true);
    },
    WelcomeIcon: undefined,
    CryIcon: undefined,
    EmptyIcon: undefined,
    HeartIcon: undefined,
    BombIcon: undefined,
    SkateIcon: undefined,
    CelebrateIcon: undefined,
};

export const SpecialEffectRenderMap: Record<SpecialEffectIconUsed, any> = {
    FaceChangeIcon: FaceChangeJSON,
    TornadoIcon: TornadoJSON,
    WelcomeIcon: WelcomeJSON,
    CryIcon: CryJSON,
    HeartIcon: HeartJSON,
    BombIcon: BombJSON,
    SkateIcon: SkateJSON,
    CelebrateIcon: CelebrateJSON,
    EmptyIcon: null,
};

export const SpecialEffectPreviewMap: Record<SpecialEffectIconUsed, number> = {
    FaceChangeIcon: 116,
    TornadoIcon: 84,
    WelcomeIcon: 56,
    CryIcon: 50,
    HeartIcon: 100,
    BombIcon: 116,
    SkateIcon: 80,
    CelebrateIcon: 46,
    EmptyIcon: 0,
};

export const SpecialEffectPreviewClassMap: Record<SpecialEffectIconUsed, string> = {
    FaceChangeIcon: "animation-size44x38",
    TornadoIcon: "animation-size38",
    WelcomeIcon: "animation-size55x36",
    CryIcon: "animation-size38",
    HeartIcon: "animation-size44x38",
    BombIcon: "animation-size44x38",
    SkateIcon: "animation-size40x38",
    CelebrateIcon: "animation-size40x38",
    EmptyIcon: "",
};

interface UseLottiePreviewReturn {
    instance: AnimationItem | null;
    play: (animationName: SpecialEffectIconUsed) => void;
    stop: () => void;
    destroy: () => void;
    updateContainer: (newContainer: HTMLElement | null) => void;
}

/**
 * Hook: 用于快速预览 Lottie 动画
 * @param containerRef 目标 DOM 元素的 ref
 */
export function useLottiePreview(
    containerRef?: React.RefObject<HTMLElement | null>,
    onAnimationEnd?: () => void,
): UseLottiePreviewReturn {
    const animationRef = useRef<AnimationItem | null>(null);
    const currentContainerRef = useRef<HTMLElement | null>(containerRef?.current);

    // 开始播放 Lottie
    const play = useCallback(
        (animationName: SpecialEffectIconUsed) => {
            if (!currentContainerRef?.current) {
                return;
            }
            const animationData = SpecialEffectRenderMap[animationName];
            // 深克隆动画数据
            const clonedData = deepClone(animationData);

            // 销毁旧实例
            if (animationRef.current) {
                animationRef.current.destroy();
                animationRef.current = null;
                currentContainerRef.current.innerHTML = "";
            }

            // 创建新实例
            animationRef.current = Lottie.loadAnimation({
                container: currentContainerRef.current,
                renderer: "svg",
                loop: false,
                autoplay: true,
                animationData: clonedData,
            });
            animationRef.current.setSubframe(false);

            const renderDoFn = SpecialEffectRenderDo[animationName];
            renderDoFn?.(animationRef.current);

            animationRef.current.addEventListener("complete", () => {
                onAnimationEnd?.();
                animationRef.current?.destroy();
                animationRef.current = null;
                if (currentContainerRef.current) {
                    currentContainerRef.current.innerHTML = "";
                }
            });
        },
        [onAnimationEnd],
    );

    // 停止播放
    const stop = useCallback(() => {
        animationRef.current?.stop();
    }, []);

    // 销毁
    const destroy = useCallback(() => {
        if (animationRef.current) {
            animationRef.current.destroy();
            animationRef.current = null;
        }
        if (containerRef?.current) {
            containerRef.current.innerHTML = "";
        }
    }, [containerRef]);

    const updateContainer = useCallback((newContainer: HTMLElement | null) => {
        if (animationRef.current) {
            // 如果当前有动画 → 先销毁，避免残留
            animationRef.current.destroy();
            animationRef.current = null;
        }
        if (currentContainerRef.current) {
            currentContainerRef.current.innerHTML = "";
        }
        currentContainerRef.current = newContainer;
    }, []);

    // 卸载时清理
    useEffect(() => {
        return () => {
            destroy();
        };
    }, [destroy]);

    return {
        instance: animationRef.current,
        play,
        stop,
        destroy,
        updateContainer,
    };
}

export function deepClone<T>(obj: T): T {
    if (typeof structuredClone === "function") {
        return structuredClone(obj);
    }
    return JSON.parse(JSON.stringify(obj));
}
